import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import {
  FAVORITE_TYPE_SYSTEM,
  FAVORITE_TYPE_USER,
  favoriteKey,
  listFavoriteModels,
  saveFavoriteModels,
} from '../services/favoriteModelsService';

const FavoriteModelsContext = createContext(null);

export function FavoriteModelsProvider({ children }) {
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const favoritesRef = useRef(favorites);

  useEffect(() => {
    favoritesRef.current = favorites;
  }, [favorites]);

  const loadFavorites = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await listFavoriteModels();
      if (!result.success) {
        setFavorites([]);
        toast.error(result.error || 'Erro ao carregar favoritos.');
        return;
      }
      setFavorites(result.data?.items ?? []);
    } catch (_error) {
      setFavorites([]);
      toast.error('Erro ao carregar favoritos.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const favoriteSet = useMemo(() => {
    return new Set(favorites.map((item) => favoriteKey(item.type, item.id)));
  }, [favorites]);

  const isFavorite = useCallback(
    (type, id) => {
      if (!type || !id) return false;
      return favoriteSet.has(favoriteKey(type, id));
    },
    [favoriteSet]
  );

  const toggleFavorite = useCallback(async (type, id) => {
    const normalizedType = String(type || '').trim().toLowerCase();
    const normalizedId = String(id || '').trim();
    if (
      (normalizedType !== FAVORITE_TYPE_SYSTEM &&
        normalizedType !== FAVORITE_TYPE_USER) ||
      !normalizedId
    ) {
      return;
    }

    const key = favoriteKey(normalizedType, normalizedId);
    const previous = favoritesRef.current;
    const exists = previous.some(
      (item) => favoriteKey(item.type, item.id) === key
    );

    const next = exists
      ? previous.filter((item) => favoriteKey(item.type, item.id) !== key)
      : [...previous, { type: normalizedType, id: normalizedId }];

    setFavorites(next);
    setIsSaving(true);

    try {
      const result = await saveFavoriteModels(next);
      if (!result.success) {
        setFavorites(previous);
        toast.error(result.error || 'Erro ao salvar favorito.');
        return;
      }
      setFavorites(result.data?.items ?? next);
    } catch (_error) {
      setFavorites(previous);
      toast.error('Erro ao salvar favorito.');
    } finally {
      setIsSaving(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      favorites,
      isLoading,
      isSaving,
      isFavorite,
      toggleFavorite,
      reload: loadFavorites,
    }),
    [favorites, isLoading, isSaving, isFavorite, toggleFavorite, loadFavorites]
  );

  return (
    <FavoriteModelsContext.Provider value={value}>
      {children}
    </FavoriteModelsContext.Provider>
  );
}

export function useFavoriteModels() {
  const context = useContext(FavoriteModelsContext);
  if (!context) {
    throw new Error(
      'useFavoriteModels must be used within FavoriteModelsProvider'
    );
  }
  return context;
}
